import { useQuery } from '@tanstack/react-query';
import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Navigation, Pagination, EffectCoverflow, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { PaginationOptions } from 'swiper/types';

import FadeInAnimation from '../../utils/FadeInAnimation';
import { getLanguageCode } from '../../utils/utils';
import DocumentsCarousel from '../Stories/DocumentsCarousel';

import { getWebContainerQuery } from '@queries/editorial';
import ErrorBoundary from '@shared/ErrorBoundary';
import PictureHelper from '@shared/PictureHelper';

import * as styles from './styles.module.scss';

const swiperNavigation = {
  nextEl: '.swiper-button-next',
  prevEl: '.swiper-button-prev',
};

const swiperPagination: PaginationOptions = {
  el: '.swiper-pagination',
  type: 'bullets',
  clickable: true,
};

const imgFormats = ['0@w'];

const Adventures = ({ containerName = 'PLAYMAKERS.ADVENTURES' }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { t } = useTranslation();
  const { locale } = useParams();

  const { data } = useQuery(getWebContainerQuery(containerName, getLanguageCode(locale)));
  const documents = useMemo(() => data.filter((d) => d.type === 'article') ?? [], [data]);
  const containers = useMemo(() => data.filter((d) => d.type === 'list') ?? [], [data]);

  useEffect(() => {
    setSelectedIndex(null);
  }, [data]);

  const handleClick = (index: number) => () => {
    setSelectedIndex(index);
  };

  const selectedContainer = useMemo(
    () => (selectedIndex >= 0 ? containers[selectedIndex]?.title.toUpperCase() : null),
    [containers, selectedIndex],
  );
  const selectedDoc = useMemo(() => (selectedIndex >= 0 ? documents[selectedIndex] : null), [documents, selectedIndex]);

  if (!documents.length) return null;

  return (
    <ErrorBoundary>
      <div id='stories' className={styles.section}>
        <div className={styles.section__content}>
          <FadeInAnimation>
            <div className={styles.container}>
              <h2 className={styles.title}>{t('stories-title2')}</h2>
              <h3 className={styles.subtitle}>{t('stories-subtitle2')}</h3>
              <div className={styles.label}>{t('stories-pick')}</div>
              {!!documents.length && !selectedDoc && (
                <Swiper
                  modules={[Navigation, Pagination, EffectCoverflow, A11y]}
                  className={styles.swiper}
                  keyboard={false}
                  centeredSlides
                  slidesPerView='auto'
                  pagination={swiperPagination}
                  navigation={swiperNavigation}
                  initialSlide={documents.length > 2 ? 1 : 0}
                >
                  <div className='slider-navigation'>
                    <div className={`swiper-button-prev ${styles.swiperButtonPrev}`}>
                      <PkIcon name='navigation-chevron-left' />
                    </div>
                    <div className={`swiper-button-next ${styles.swiperButtonNext}`}>
                      <PkIcon name='navigation-chevron-right' />
                    </div>
                  </div>
                  <meta itemProp='numberOfItems' content={`${documents.length}`} />
                  {documents.map((d, i) => (
                    <SwiperSlide onClick={handleClick(i)} key={`doc_${d.id}`}>
                      {({ isActive, isPrev, isNext }) => (
                        <div
                          className={clsx({
                            [styles.card]: true,
                            [styles.cardActive]: isActive,
                            [styles.cardPrev]: isPrev,
                            [styles.cardNext]: isNext,
                          })}
                        >
                          <PictureHelper
                            className={styles.card__image}
                            image={d.mainMedia.image}
                            title={d.title}
                            fallback={`${window.imgBaseUrl}/imgml/uefacom/void` + '.svg'}
                            formats={imgFormats}
                          />
                          <div className={styles.card__title}>{d.title}</div>
                        </div>
                      )}
                    </SwiperSlide>
                  ))}
                  <div className={`swiper-pagination ${styles.swiperPagination}`} />
                </Swiper>
              )}
              {selectedDoc && (
                <>
                  <button type='button' className={styles.selectedDocument} onClick={handleClick(null)}>
                    <span className={styles.selectedDocumentLabel}>{selectedDoc.title}</span>
                    <PkIcon className={styles.selectedDocumentClose} name='close-web-icon' />
                  </button>
                  <div className={styles.label}>{t('stories-download')}</div>
                  <DocumentsCarousel containerName={selectedContainer} />
                </>
              )}
            </div>
          </FadeInAnimation>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Adventures;
